import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { PublicClientApplication, AccountInfo } from '@azure/msal-browser'
import { msalConfig, loginRequest } from './msalConfig'
import { setAuthToken } from './api/client'

interface AuthCtx {
  account:  AccountInfo | null
  ready:    boolean
  login:    () => Promise<void>
  logout:   () => void
  getToken: () => Promise<string>
}

export const msalInstance = new PublicClientApplication(msalConfig)

const Ctx = createContext<AuthCtx>({} as AuthCtx)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<AccountInfo | null>(null)
  const [ready,   setReady]   = useState(false)

  useEffect(() => {
    msalInstance.initialize().then(async () => {
      const result = await msalInstance.handleRedirectPromise().catch(() => null)
      if (result?.account) {
        setAccount(result.account)
        setAuthToken(result.accessToken)
      } else {
        const all = msalInstance.getAllAccounts()
        if (all && all.length > 0) {
          setAccount(all[0])
          try {
            const r = await msalInstance.acquireTokenSilent({ ...loginRequest, account: all[0] })
            setAuthToken(r.accessToken)
          } catch { /* getToken() will retry on demand */ }
        }
      }
      setReady(true)
    })
  }, [])

  async function getToken(): Promise<string> {
    const acc = account || msalInstance.getAllAccounts()?.[0]
    if (!acc) return ''
    try {
      const r = await msalInstance.acquireTokenSilent({ ...loginRequest, account: acc })
      setAuthToken(r.accessToken)
      return r.accessToken
    } catch {
      const r = await msalInstance.acquireTokenPopup(loginRequest)
      setAuthToken(r.accessToken)
      return r.accessToken
    }
  }

  async function login() {
    const r = await msalInstance.loginPopup(loginRequest)
    setAccount(r.account)
    setAuthToken(r.accessToken)
  }

  function logout() {
    msalInstance.logoutPopup()
    setAccount(null)
    setAuthToken('')
  }

  return (
    <Ctx.Provider value={{ account, ready, login, logout, getToken }}>
      {children}
    </Ctx.Provider>
  )
}

export function useAuth() {
  return useContext(Ctx)
}
